<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { XMarkIcon, Bars3Icon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import CoverArtTile from '@/components/CoverArtTile.vue';
import TrackCard from '@/components/TrackCard.vue';
import DraggableContainer from '@/components/ui/draggable/DraggableContainer.vue';
import { TRACK_CARD_DENSITIES, useTrackCardDensity } from '@/composables/trackCardDensity';
import { useAudioPlaybackStore } from '@/stores/playerStore';
import type { QueueEntry } from '@/types/playerQueue';

/**
 * The play queue, pushed onto the inspector stack by the player bar's Queue
 * button.
 *
 * Rows are keyed by `entryId`, never by track code: a generated playlist may
 * name the same track three times and those are three independently removable,
 * reorderable rows.
 */
const playback = useAudioPlaybackStore();
const { queueEntries, currentQueueIndex, queueTotalCount, queueTruncated, queueLabel } =
    storeToRefs(playback);

const upcomingCount = computed(() => Math.max(0, queueEntries.value.length - currentQueueIndex.value - 1));

/**
 * What the queue holds versus what the list behind it had.
 *
 * Two different ways the queue can be smaller than the list, and both have to
 * be visible or a partial queue reads as the whole thing:
 *
 *  - the cap cut it — "First 500 of 12,431";
 *  - tracks with no audio were dropped when it was built, which has nothing to
 *    do with the cap and so leaves `truncated` false — "350 playable of 400".
 */
const countLine = computed(() => {
    const shown = queueEntries.value.length;
    const total = queueTotalCount.value;

    if (queueTruncated.value && total > shown) {
        return `First ${shown.toLocaleString()} of ${total.toLocaleString()}`;
    }

    if (total > shown) {
        return `${shown.toLocaleString()} playable of ${total.toLocaleString()}`;
    }

    return shown === 1 ? '1 track' : `${shown.toLocaleString()} tracks`;
});

/**
 * The SAME shared preference the track lists use, not a queue-local one.
 *
 * Switching a bucket's Tracks tab to `compact` and then opening the queue to
 * find `full` cards would be two controls with one label disagreeing about what
 * the user just asked for.
 */
const density = useTrackCardDensity();

const DENSITY_LABELS: Record<(typeof TRACK_CARD_DENSITIES)[number], string> = {
    compact: 'Compact',
    default: 'Default',
    full: 'Full',
};

function isCurrent(index: number): boolean {
    return index === currentQueueIndex.value;
}

/** The drag container hands back reordered entries; the store re-seats by id. */
function onReorder(reordered: QueueEntry[]): void {
    playback.reorderQueue(reordered.map((entry) => entry.entryId));
}
</script>

<template>
    <div class="flex min-h-0 flex-col gap-3 p-4" data-testid="queue-inspector">
        <!--
          The list this queue came from, when it came from one. There is no
          fallback line: the inspector's own title already says "Queue", so
          "Play queue" under it stated the same thing twice.
        -->
        <p v-if="queueLabel" class="min-w-0 truncate text-sm font-medium" data-testid="queue-label">
            {{ queueLabel }}
        </p>

        <!-- Same toolbar shape as a track list: density on the left, actions and
             then the count on the right. -->
        <div class="flex flex-wrap items-center justify-between gap-2">
            <div
                class="bg-muted inline-flex items-center rounded-lg p-[3px] text-xs"
                role="group"
                aria-label="Track card density"
            >
                <button
                    v-for="option in TRACK_CARD_DENSITIES"
                    :key="option"
                    type="button"
                    class="cursor-pointer rounded-md px-2.5 py-1 font-medium transition-colors"
                    :class="
                        density === option
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    "
                    :aria-pressed="density === option"
                    :data-testid="`queue-density-${option}`"
                    @click="density = option"
                >
                    {{ DENSITY_LABELS[option] }}
                </button>
            </div>

            <!-- `ml-auto` as well as the parent's justify-between: the inspector
                 panel is narrow enough that this group wraps to its own line,
                 and a wrapped flex line ignores justify-between. Without it the
                 count jumps to the left the moment the panel narrows. -->
            <div class="ml-auto flex items-center gap-3">
                <Button
                    v-if="upcomingCount > 0"
                    type="button"
                    variant="outline"
                    size="sm"
                    class="shrink-0"
                    data-testid="queue-clear-upcoming"
                    @click="playback.clearUpcoming()"
                >
                    Clear upcoming
                </Button>
                <p
                    class="text-muted-foreground shrink-0 text-xs tabular-nums"
                    data-testid="queue-count-line"
                >
                    {{ countLine }}
                </p>
            </div>
        </div>

        <p v-if="queueEntries.length === 0" class="text-muted-foreground text-sm">
            Nothing queued.
        </p>

        <DraggableContainer
            v-else
            :model-value="queueEntries"
            item-key="entryId"
            handle=".queue-drag-handle"
            class="flex min-h-0 flex-col gap-1 overflow-y-auto"
            @update:model-value="onReorder"
        >
            <template #item="{ element, index }">
                <div
                    class="hover:bg-muted/60 relative flex items-center gap-2 rounded-md px-1 py-1"
                    :class="isCurrent(index) ? 'bg-muted' : ''"
                    :aria-current="isCurrent(index) ? 'true' : undefined"
                    :data-testid="`queue-entry-${element.entryId}`"
                >
                    <!--
                      Now playing, as an absolutely positioned stripe.
                      This used to be a speaker icon in the row's flex flow, so
                      the playing row's cover, title and everything after it sat
                      a icon's width further right than every other row — the
                      whole list appeared to shift as the queue advanced. Taken
                      out of flow, it cannot move anything.

                      Decorative: `aria-label` on an empty span with no role is
                      not exposed by assistive tech, so this said nothing to a
                      screen reader and neither did the speaker icon it
                      replaced. `aria-current` on the row carries that meaning
                      now, and costs no layout either.
                    -->
                    <span
                        v-if="isCurrent(index)"
                        class="bg-primary absolute inset-y-1 left-0 w-[3px] rounded-full"
                        data-testid="queue-now-playing"
                        aria-hidden="true"
                    />
                    <span
                        class="queue-drag-handle text-muted-foreground hover:text-foreground shrink-0 cursor-grab px-1"
                        aria-hidden="true"
                    >
                        <Bars3Icon class="h-4 w-4" />
                    </span>

                    <!--
                      The WHOLE card seats the player on this entry. The card's
                      own targets are switched off (`host-handles-click`) because
                      in a queue none of them is what a click means — the cover
                      keeps only its zoom, which stops its own click.

                      A div rather than a <button>: a card legitimately contains
                      buttons (the cover's expand, a tag chip's tooltip trigger),
                      and a button inside a button is invalid HTML that browsers
                      resolve by dropping the inner one. The role and key handler
                      give it the keyboard behaviour a button would have had.
                    -->
                    <div
                        v-if="element.card"
                        class="min-w-0 flex-1 cursor-pointer"
                        role="button"
                        tabindex="0"
                        :aria-label="`Play ${element.title ?? element.code}`"
                        :data-testid="`queue-jump-${element.entryId}`"
                        @click="playback.jumpToEntry(element.entryId)"
                        @keydown.self.enter.prevent="playback.jumpToEntry(element.entryId)"
                        @keydown.self.space.prevent="playback.jumpToEntry(element.entryId)"
                    >
                        <!--
                          `position-override` is the QUEUE's numbering, not the
                          list position the row was stamped with — those diverge
                          the moment anything is reordered, removed or shuffled,
                          and it is the queue order the user is looking at.
                        -->
                        <TrackCard
                            :track="element.card"
                            :density="density"
                            :queue-entry-id="element.entryId"
                            :position-override="index + 1"
                            :failure-reason="element.failureReason"
                            host-handles-click
                            flush
                        />
                    </div>

                    <!--
                      No card: this entry did not come from a list, so there is
                      nothing richer to render. A Shift-enqueue from Track
                      Matching, a one-off diagnostics play, a local file. The
                      original row, unchanged.
                    -->
                    <button
                        v-else
                        type="button"
                        class="flex min-w-0 flex-1 items-center gap-2 text-left"
                        :aria-label="`Play ${element.title ?? element.code}`"
                        :data-testid="`queue-jump-${element.entryId}`"
                        @click="playback.jumpToEntry(element.entryId)"
                    >
                        <CoverArtTile
                            :src="element.coverUrl"
                            :alt="element.title ? `Cover art for ${element.title}` : 'Cover art'"
                            :size="36"
                            class="shrink-0"
                            :failure-reason="element.failureReason"
                            :failure-testid="`queue-failed-${element.entryId}`"
                        />
                        <span class="min-w-0 flex-1">
                            <!-- `block`, because `truncate` does nothing to an
                                 inline element — the title ran full width and
                                 slid under the failure icon and the remove
                                 button sitting after it. -->
                            <span
                                class="block truncate text-sm"
                                :class="isCurrent(index) ? 'font-medium' : ''"
                            >
                                {{ element.title ?? element.code }}
                            </span>
                            <span class="text-muted-foreground block truncate text-xs">
                                {{ element.artist ?? 'Unknown artist' }}
                            </span>
                        </span>
                    </button>

                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        class="h-7 w-7 shrink-0"
                        :aria-label="`Remove ${element.title ?? element.code} from the queue`"
                        :data-testid="`queue-remove-${element.entryId}`"
                        @click="playback.removeEntry(element.entryId)"
                    >
                        <XMarkIcon class="h-4 w-4" />
                    </Button>
                </div>
            </template>
        </DraggableContainer>
    </div>
</template>
